/* ⚠️ GENERATED from app/services/searchRank.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   🔎 searchRank.ts — how a search query is matched and ranked. One rule for the
   server (which answers every search) and both clients (which highlight and,
   offline, fall back to what they already hold).

   🟥 THE APP COULD ONLY SEARCH VIDEOS, and only the ones already on the phone;
      the browser searched the newest 400 posts it had downloaded; people search
      matched usernames but never a full name. Three searches, three answers.
   🟢 WHAT "SMART" MEANS (carried over from the browser's search, which was the
      good one):
        · diacritics folded — "cafe" finds "café"; Arabic, Cyrillic, CJK kept
        · every word must match SOMETHING (AND), so two words narrow
        · a word matches whole, as a prefix, inside a word, or with one typo —
          in that order of confidence
        · fields are weighted: a link title beats a caption beats a track name
        · the whole phrase intact beats its words scattered
        · ties break on freshness and engagement; friends a little higher
   Plain logic, no imports: the kind of a post is restated from postGrid's
   postKind (the suite compares the two), so this runs anywhere unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

export const SEARCH_KINDS = ['all', 'people', 'video', 'photo', 'poll', 'text', 'link'];
export const RESULT_MAX = 60;

/** lowercase, accent-folded, punctuation-flattened — the one normaliser. */
export function fold(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9؀-ۿЀ-ӿ一-鿿぀-ヿऀ-ॿ]+/g, ' ').trim();
}

/** One edit apart? Bails at the second difference. */
export function within1(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (la > lb) i += 1;else if (lb > la) j += 1;else {
      i += 1;
      j += 1;
    }
  }
  return true;
}

/** What a post is — postGrid.postKind, restated (a poll outranks its media). */
export function kindOf(post) {
  const p = post || {};
  if (p.type === 'poll' && Array.isArray(p.pollOptions) && p.pollOptions.length > 0) return 'poll';
  const media = Array.isArray(p.media) ? p.media : [];
  if (media.some(m => m && m.type === 'video') || p.videoUrl) return 'video';
  if (p.linkUrl) return 'link';
  if (media.length || p.imageUrl) return 'photo';
  return 'text';
}
/** The searchable text of a post, each with its weight. `tags` = its hashtags. */
export function fieldsOf(p, tags = []) {
  const out = [];
  const add = (text, w) => {
    const t = fold(text);
    if (t) out.push({
      t,
      toks: t.split(' '),
      w
    });
  };
  add(p.linkTitle, 4.5);
  add(p.pollQuestion, 4);
  if (Array.isArray(p.pollOptions)) add(p.pollOptions.map(o => o ? o.text || o.label || '' : '').join(' '), 2.2);
  add(p.linkDomain || p.linkUrl, 3);
  add(p.content, 3.2);
  add(p.overlayText, 2.6);
  add(p.userName, 3);
  add(p.userUsername || p.username, 3);
  add(p.pageName, 2.4);
  add(p.pageCategory, 1.6);
  add(p.locationLabel, 2);
  add(p.audioTrackTitle, 1.8);
  add(p.status && (p.status.location || p.status.to || p.status.from), 1.6);
  add(tags.join(' '), 3.4);
  add(Array.isArray(p.aiTags) ? p.aiTags.join(' ') : '', 1.4);
  return out;
}
export function fieldScore(f, term) {
  if (f.t === term) return 1;
  let best = 0;
  for (const tok of f.toks) {
    if (tok === term) return 1;
    if (tok.startsWith(term)) {
      best = Math.max(best, 0.82);
      continue;
    }
    if (term.length >= 4 && within1(tok, term)) best = Math.max(best, 0.62);
  }
  if (!best && term.length >= 3 && f.t.includes(term)) best = 0.5;
  return best;
}
const tsOf = (p, now) => {
  const t = p && p.timestamp;
  if (!t) return now;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.toDate === 'function') return t.toDate().getTime();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  return now;
};
/** Rank posts against a query. Returns the posts with `_score`, best first. */
export function rankPosts(posts, raw, opts = {}) {
  const kind = opts.kind || 'all';
  const now = typeof opts.now === 'number' ? opts.now : Date.now();
  const tagsOf = opts.tagsOf || (p => Array.isArray(p && p.hashtags) ? p.hashtags : []);
  const phrase = fold(raw);
  const terms = phrase.split(' ').filter(Boolean);
  const only = opts.onlyTag || null;
  if (!terms.length && !only) return [];
  const out = [];
  for (const p of posts || []) {
    if (!p) continue;
    if (kind !== 'all' && kind !== 'people' && kindOf(p) !== kind) continue;
    const tags = tagsOf(p) || [];
    if (only && tags.indexOf(only) < 0) continue;
    const fields = fieldsOf(p, tags);
    if (!fields.length && !only) continue;
    let total = 0;
    let ok = true;
    if (!only) {
      for (const term of terms) {
        let best = 0;
        for (const f of fields) {
          const s = fieldScore(f, term) * f.w;
          if (s > best) best = s;
        }
        if (best <= 0) {
          ok = false;
          break;
        }
        total += best;
      }
      if (!ok) continue;
      if (terms.length > 1 && fields.some(f => f.t.includes(phrase))) total += 6;
    }
    const ts = tsOf(p, now);
    const days = Math.max(0, (now - ts) / 864e5);
    total += 3 / (1 + days / 7);
    const likes = Math.max(Array.isArray(p.likes) ? p.likes.length : 0, Number(p.likesCount) || 0);
    const comments = Math.max(Array.isArray(p.comments) ? p.comments.length : 0, Number(p.commentsCount) || 0);
    total += Math.min(3, (likes + comments * 2 + (Number(p.sharesCount) || 0) * 3) / 25);
    const isPage = p.authorType === 'page' || !!p.pageId;
    if (!isPage && opts.isFriend && p.userId && opts.isFriend(String(p.userId))) total += 1.5;
    out.push(Object.assign({}, p, {
      _score: total,
      _ts: ts,
      _kind: kindOf(p)
    }));
  }
  out.sort((a, b) => b._score - a._score || b._ts - a._ts);
  return out.slice(0, opts.max || RESULT_MAX);
}

/** Rank people (or pages) by name and username. Each: { uid, name, username }. */
export function rankPeople(people, raw, opts = {}) {
  const terms = fold(raw).split(' ').filter(Boolean);
  if (!terms.length) return [];
  const out = [];
  for (const u of people || []) {
    if (!u) continue;
    const fields = [];
    const add = (text, w) => {
      const t = fold(text);
      if (t) fields.push({
        t,
        toks: t.split(' '),
        w
      });
    };
    add(u.name, 3.2);
    add(u.username, 3);
    add(u.category, 1);
    let total = 0;
    let ok = true;
    for (const term of terms) {
      let best = 0;
      for (const f of fields) {
        const s = fieldScore(f, term) * f.w;
        if (s > best) best = s;
      }
      if (best <= 0) {
        ok = false;
        break;
      }
      total += best;
    }
    if (!ok) continue;
    if (opts.isFriend && u.uid && opts.isFriend(String(u.uid))) total += 2;
    out.push(Object.assign({}, u, {
      _score: total
    }));
  }
  out.sort((a, b) => b._score - a._score || String(a.name || '').localeCompare(String(b.name || '')));
  return out.slice(0, opts.max || 20);
}

/** The words of a name, folded — what the server stores for people search (`nameWords`). */
export function nameWords(...names) {
  const set = new Set();
  for (const n of names) for (const w of fold(n).split(' ')) if (w && w.length >= 1) set.add(w);
  return Array.from(set).slice(0, 20);
}

/** Split text into [{text, hit}] so the matched words can be marked up. */
export function highlightParts(text, raw) {
  const src = String(text || '');
  const terms = fold(raw).split(' ').filter(t => t.length >= 2);
  if (!src || !terms.length) return [{
    text: src,
    hit: false
  }];
  const chars = [];
  for (let i = 0; i < src.length; i += 1) {
    const f = fold(src[i]);
    chars.push(f === '' ? ' ' : f[0]);
  }
  const folded = chars.join('');
  const marks = new Array(src.length).fill(false);
  for (const term of terms) {
    let from = 0;
    for (;;) {
      const at = folded.indexOf(term, from);
      if (at < 0) break;
      for (let i = at; i < at + term.length && i < marks.length; i += 1) marks[i] = true;
      from = at + term.length;
    }
  }
  const parts = [];
  let cur = null;
  for (let i = 0; i < src.length; i += 1) {
    if (!cur || cur.hit !== marks[i]) {
      cur = {
        text: '',
        hit: marks[i]
      };
      parts.push(cur);
    }
    cur.text += src[i];
  }
  return parts;
}
